import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors, shadows, borderRadius, gradients } from '../theme/designSystem';
import { configService } from '../services/api';
import { PricingConfig, ThresholdsConfig, NotificationsConfig, MqttConfig, DatabaseConfig, WebSocketConfig, TableMapping, DatabaseSchema, TargetField } from '../types';
import axios from 'axios';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Test connection states
  const [testingMqtt, setTestingMqtt] = useState(false);
  const [testingDatabase, setTestingDatabase] = useState(false);
  const [testingWebSocket, setTestingWebSocket] = useState(false);
  const [mqttTestResult, setMqttTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [databaseTestResult, setDatabaseTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [websocketTestResult, setWebsocketTestResult] = useState<{ success: boolean; message: string } | null>(null);
  
  // User Management States
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    roles: [] as string[],
  });
  
  // Separate state for each config section
  const [pricing, setPricing] = useState<PricingConfig>({
    gas_price_per_m3: 0.50,
    feedstock_cost_per_kg: 0.02,
    electricity_rate_per_kwh: 0.15,
    power_billing_rate_php_kwh: 7.5,
    gas_billing_rate_php_nm3: 13.5,
  });
  
  const [thresholds, setThresholds] = useState<ThresholdsConfig>({
    temp_min: 30,
    temp_max: 60,
    ph_min: 6.5,
    ph_max: 8.0,
    yield_min: 0.3,
    yield_max: 0.6,
  });
  
  const [notifications, setNotifications] = useState<NotificationsConfig>({
    enable_alerts: true,
    enable_email_reports: false,
    refresh_interval: 5,
  });

  // Connection configuration states
  const [mqttConfig, setMqttConfig] = useState<MqttConfig>({
    broker_url: 'mqtt://localhost:1883',
    client_id_prefix: 'biogas_backend',
    username: '',
    password: '',
    enabled: true,
  });

  const [databaseConfig, setDatabaseConfig] = useState<DatabaseConfig>({
    host: 'localhost',
    port: 5432,
    database: 'biogas_plant',
    user: 'biogas_user',
    password: '',
  });

  const [websocketConfig, setWebsocketConfig] = useState<WebSocketConfig>({
    port: 4001,
    enabled: true,
  });

  // Database mapping states
  const [tableMappings, setTableMappings] = useState<TableMapping[]>([]);
  const [databaseSchema, setDatabaseSchema] = useState<DatabaseSchema | null>(null);
  const [targetEntities, setTargetEntities] = useState<string[]>([]);
  const [targetFields, setTargetFields] = useState<TargetField[]>([]);
  const [discoveringSchema, setDiscoveringSchema] = useState(false);
  const [editingMapping, setEditingMapping] = useState<TableMapping | null>(null);
  const [openMappingDialog, setOpenMappingDialog] = useState(false);

  // Tag import states
  const [tagTableName, setTagTableName] = useState('tbltagnames');
  const [tagColumnName, setTagColumnName] = useState('tagname');
  const [tagUnitColumn, setTagUnitColumn] = useState('unit');
  const [discoveringTags, setDiscoveringTags] = useState(false);
  const [autoMappingTags, setAutoMappingTags] = useState(false);
  const [discoveredTags, setDiscoveredTags] = useState<any[]>([]);
  const [tagMappingSuggestions, setTagMappingSuggestions] = useState<any[]>([]);
  const [selectedMappings, setSelectedMappings] = useState<any[]>([]);
  const [syncingData, setSyncingData] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ isSyncing: boolean } | null>(null);

  // Billing rate management
  const [billingRates, setBillingRates] = useState<any[]>([]);
  const [newRate, setNewRate] = useState({ rateType: 'power', rateValue: '', effectiveDate: '' });
  const [savingRate, setSavingRate] = useState(false);

  const loadBillingRates = async () => {
    try {
      const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';
      const res = await axios.get(`${API}/billing-rates`);
      setBillingRates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading billing rates:', err);
    }
  };

  const handleSaveRate = async () => {
    if (!newRate.rateValue || !newRate.effectiveDate) return;
    setSavingRate(true);
    try {
      const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';
      await axios.post(`${API}/billing-rates`, {
        rateType: newRate.rateType,
        rateValue: parseFloat(newRate.rateValue),
        effectiveDate: newRate.effectiveDate,
      });
      setNewRate({ rateType: 'power', rateValue: '', effectiveDate: '' });
      await loadBillingRates();
      setShowSuccess(true);
    } catch (err: any) {
      setErrorMessage(err?.response?.data?.message || 'Failed to save rate');
      setShowError(true);
    } finally {
      setSavingRate(false);
    }
  };

  const handleDeleteRate = async (id: number) => {
    try {
      const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';
      await axios.delete(`${API}/billing-rates/${id}`);
      await loadBillingRates();
      setShowSuccess(true);
    } catch {
      setErrorMessage('Failed to delete rate');
      setShowError(true);
    }
  };

  useEffect(() => {
    const loadInitialSettings = async () => {
      setLoading(true);
      try {
        const [pricingData, thresholdsData, notificationsData, mqttData, databaseData, websocketData] = await Promise.all([
          configService.getPricing(),
          configService.getThresholds(),
          configService.getNotifications(),
          configService.getMqttConfig(),
          configService.getDatabaseConfig(),
          configService.getWebSocketConfig(),
        ]);
        setPricing(pricingData);
        setThresholds(thresholdsData);
        setNotifications(notificationsData);
        setMqttConfig(mqttData);
        setDatabaseConfig(databaseData);
        setWebsocketConfig(websocketData);
      } catch (err) {
        console.error('Error loading settings:', err);
        setErrorMessage('Failed to load settings from server');
        setShowError(true);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialSettings();
    loadBillingRates();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      loadTableMappings();
      loadTargetEntities();
    }
    if (activeTab === 2) {
      loadTableMappings();
      loadTargetEntities();
    }
    if (activeTab === 4) {
      loadUsers();
      loadRoles();
    }
  }, [activeTab]);

  const loadTableMappings = async () => {
    try {
      const mappings = await configService.getTableMappings();
      setTableMappings(mappings || []);
    } catch (err) {
      console.error('Error loading table mappings:', err);
    }
  };

  const loadTargetEntities = async () => {
    try {
      const entities = await configService.getTargetEntities();
      setTargetEntities(entities);
    } catch (err) {
      console.error('Error loading target entities:', err);
    }
  };

  const loadTargetFieldsForEntity = async (entity: string) => {
    try {
      const fields = await configService.getTargetFields(entity);
      setTargetFields(fields);
    } catch (err) {
      console.error('Error loading target fields:', err);
      setTargetFields([]);
    }
  };

  const handleDiscoverSchema = async () => {
    setDiscoveringSchema(true);
    try {
      console.log('Discovering schema with config:', databaseConfig);
      const result = await configService.discoverDatabaseSchema(databaseConfig);
      console.log('Schema discovery result:', result);
      
      if (result.success && result.schema) {
        setDatabaseSchema(result.schema);
        setShowSuccess(true);
      } else {
        setErrorMessage(result.message || 'Failed to discover database schema');
        setShowError(true);
      }
    } catch (err: any) {
      console.error('Schema discovery error:', err);
      setErrorMessage(err.response?.data?.message || err.message || 'Failed to discover database schema');
      setShowError(true);
    } finally {
      setDiscoveringSchema(false);
    }
  };

  const handleSaveMappings = async () => {
    try {
      await configService.updateTableMappings(tableMappings);
      setShowSuccess(true);
    } catch (err) {
      setErrorMessage('Failed to save table mappings');
      setShowError(true);
    }
  };

  const handleAddMapping = () => {
    setEditingMapping({
      source_table: '',
      target_entity: '',
      columns: [],
    });
    setOpenMappingDialog(true);
  };

  const handleEditMapping = async (mapping: TableMapping) => {
    setEditingMapping(mapping);
    if (mapping.target_entity) {
      await loadTargetFieldsForEntity(mapping.target_entity);
    }
    setOpenMappingDialog(true);
  };

  const handleDeleteMapping = (index: number) => {
    const updated = tableMappings.filter((_, i) => i !== index);
    setTableMappings(updated);
  };

  const handleSaveMapping = () => {
    if (!editingMapping) return;

    const existingIndex = tableMappings.findIndex(
      m => m.source_table === editingMapping.source_table && m.target_entity === editingMapping.target_entity
    );

    if (existingIndex >= 0) {
      const updated = [...tableMappings];
      updated[existingIndex] = editingMapping;
      setTableMappings(updated);
    } else {
      setTableMappings([...tableMappings, editingMapping]);
    }

    setOpenMappingDialog(false);
    setEditingMapping(null);
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get('http://localhost:4001/auth/users', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setUsers(response.data);
    } catch (err: any) {
      console.error('Error loading users:', err);
      if (err.response?.status === 401) {
        setErrorMessage('Please login to access user management');
        setShowError(true);
        setActiveTab(0); // Switch back to first tab
      }
    }
  };

  const loadRoles = async () => {
    try {
      const response = await axios.get('http://localhost:4001/auth/roles/all', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setRoles(response.data);
    } catch (err: any) {
      console.error('Error loading roles:', err);
      if (err.response?.status === 401) {
        setErrorMessage('Please login to access role management');
        setShowError(true);
      }
    }
  };

  const handleCreateUser = async () => {
    try {
      await axios.post('http://localhost:4001/auth/register', newUser, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setOpenUserDialog(false);
      setNewUser({ username: '', email: '', password: '', roles: [] });
      loadUsers();
      setShowSuccess(true);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to create user');
      setShowError(true);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`http://localhost:4001/auth/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        loadUsers();
        setShowSuccess(true);
      } catch (err) {
        setErrorMessage('Failed to delete user');
        setShowError(true);
      }
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [pricingData, thresholdsData, notificationsData, mqttData, databaseData, websocketData] = await Promise.all([
        configService.getPricing(),
        configService.getThresholds(),
        configService.getNotifications(),
        configService.getMqttConfig(),
        configService.getDatabaseConfig(),
        configService.getWebSocketConfig(),
      ]);
      setPricing(pricingData);
      setThresholds(thresholdsData);
      setNotifications(notificationsData);
      setMqttConfig(mqttData);
      setDatabaseConfig(databaseData);
      setWebsocketConfig(websocketData);
    } catch (err) {
      console.error('Error loading settings:', err);
      setErrorMessage('Failed to load settings from server');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await Promise.all([
        configService.updatePricing(pricing),
        configService.updateThresholds(thresholds),
        configService.updateNotifications(notifications),
      ]);
      setShowSuccess(true);
    } catch (err) {
      console.error('Error saving settings:', err);
      setErrorMessage('Failed to save settings');
      setShowError(true);
    }
  };

  const handleReset = () => {
    loadSettings();
  };

  const handleSaveConnections = async () => {
    try {
      await Promise.all([
        configService.updateMqttConfig(mqttConfig),
        configService.updateDatabaseConfig(databaseConfig),
        configService.updateWebSocketConfig(websocketConfig),
      ]);
      setShowSuccess(true);
    } catch (err) {
      console.error('Error saving connection settings:', err);
      setErrorMessage('Failed to save connection settings');
      setShowError(true);
    }
  };

  const handleTestMqtt = async () => {
    setTestingMqtt(true);
    setMqttTestResult(null);
    try {
      const result = await configService.testMqttConnection(mqttConfig);
      setMqttTestResult(result);
      if (result.success) {
        setShowSuccess(true);
      } else {
        setErrorMessage(result.message);
        setShowError(true);
      }
    } catch (err: any) {
      setMqttTestResult({ success: false, message: err.message || 'Failed to test connection' });
      setErrorMessage('Failed to test MQTT connection');
      setShowError(true);
    } finally {
      setTestingMqtt(false);
    }
  };

  const handleTestDatabase = async () => {
    setTestingDatabase(true);
    setDatabaseTestResult(null);
    try {
      const result = await configService.testDatabaseConnection(databaseConfig);
      setDatabaseTestResult(result);
      if (result.success) {
        setShowSuccess(true);
      } else {
        setErrorMessage(result.message);
        setShowError(true);
      }
    } catch (err: any) {
      setDatabaseTestResult({ success: false, message: err.message || 'Failed to test connection' });
      setErrorMessage('Failed to test database connection');
      setShowError(true);
    } finally {
      setTestingDatabase(false);
    }
  };

  const handleTestWebSocket = async () => {
    setTestingWebSocket(true);
    setWebsocketTestResult(null);
    try {
      const result = await configService.testWebSocketConfig(websocketConfig);
      setWebsocketTestResult(result);
      if (result.success) {
        setShowSuccess(true);
      } else {
        setErrorMessage(result.message);
        setShowError(true);
      }
    } catch (err: any) {
      setWebsocketTestResult({ success: false, message: err.message || 'Failed to test configuration' });
      setErrorMessage('Failed to test WebSocket configuration');
      setShowError(true);
    } finally {
      setTestingWebSocket(false);
    }
  };

  const handleDiscoverTags = async () => {
    setDiscoveringTags(true);
    try {
      const result = await configService.discoverTags(databaseConfig, tagTableName, tagColumnName, tagUnitColumn);
      if (result.success && result.tags) {
        setDiscoveredTags(result.tags);
        setShowSuccess(true);
      } else {
        setErrorMessage(result.message || 'Failed to discover tags');
        setShowError(true);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to discover tags');
      setShowError(true);
    } finally {
      setDiscoveringTags(false);
    }
  };

  const handleAutoMapTags = async () => {
    if (discoveredTags.length === 0) {
      setErrorMessage('Please discover tags first');
      setShowError(true);
      return;
    }

    setAutoMappingTags(true);
    try {
      const suggestions = await configService.autoMapTags(discoveredTags);
      setTagMappingSuggestions(suggestions);
      // Pre-select all suggestions with confidence > 0.7
      const highConfidence = suggestions.filter((s: any) => s.confidence > 0.7);
      setSelectedMappings(highConfidence);
      setShowSuccess(true);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to auto-map tags');
      setShowError(true);
    } finally {
      setAutoMappingTags(false);
    }
  };

  const handleSaveTagMappings = async () => {
    if (selectedMappings.length === 0) {
      setErrorMessage('Please select at least one mapping');
      setShowError(true);
      return;
    }

    try {
      await configService.updateTagMappings(selectedMappings);
      setShowSuccess(true);
      // Reset states
      setDiscoveredTags([]);
      setTagMappingSuggestions([]);
      setSelectedMappings([]);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to save tag mappings');
      setShowError(true);
    }
  };

  const handleManualSync = async () => {
    setSyncingData(true);
    try {
      const result = await configService.manualSync();
      if (result.success) {
        setShowSuccess(true);
      } else {
        setErrorMessage(result.message);
        setShowError(true);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to sync data');
      setShowError(true);
    } finally {
      setSyncingData(false);
    }
  };

  const toggleMappingSelection = (mapping: any) => {
    const isSelected = selectedMappings.some(m => m.tag_name === mapping.tag_name);
    if (isSelected) {
      setSelectedMappings(selectedMappings.filter(m => m.tag_name !== mapping.tag_name));
    } else {
      setSelectedMappings([...selectedMappings, mapping]);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: colors.primary[600] }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
        System Settings
      </Typography>

      <Paper sx={{ mb: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
            },
            '& .Mui-selected': {
              color: colors.primary[600],
            },
          }}
        >
          <Tab label="System Configuration" />
          <Tab label="Data Connections" />
          <Tab label="Database Mapping" />
          <Tab label="Tag Import & Sync" />
          <Tab label="User Management (RBAC)" />
        </Tabs>
      </Paper>

      {/* System Configuration Tab */}
      {activeTab === 0 && (
        <>
        <Grid container spacing={3}>
        {/* Target Ranges */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
              Optimal Operating Ranges
            </Typography>

            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.neutral[700], mb: 2 }}>
              Temperature (°C)
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Minimum"
                  type="number"
                  value={thresholds.temp_min}
                  onChange={(e) => setThresholds({ ...thresholds, temp_min: parseFloat(e.target.value) })}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Maximum"
                  type="number"
                  value={thresholds.temp_max}
                  onChange={(e) => setThresholds({ ...thresholds, temp_max: parseFloat(e.target.value) })}
                  size="small"
                />
              </Grid>
            </Grid>

            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.neutral[700], mb: 2 }}>
              pH Level
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Minimum"
                  type="number"
                  value={thresholds.ph_min}
                  onChange={(e) => setThresholds({ ...thresholds, ph_min: parseFloat(e.target.value) })}
                  size="small"
                  inputProps={{ step: 0.1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Maximum"
                  type="number"
                  value={thresholds.ph_max}
                  onChange={(e) => setThresholds({ ...thresholds, ph_max: parseFloat(e.target.value) })}
                  size="small"
                  inputProps={{ step: 0.1 }}
                />
              </Grid>
            </Grid>

            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.neutral[700], mb: 2 }}>
              Gas Yield (m³/kg)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Minimum"
                  type="number"
                  value={thresholds.yield_min}
                  onChange={(e) => setThresholds({ ...thresholds, yield_min: parseFloat(e.target.value) })}
                  size="small"
                  inputProps={{ step: 0.1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Maximum"
                  type="number"
                  value={thresholds.yield_max}
                  onChange={(e) => setThresholds({ ...thresholds, yield_max: parseFloat(e.target.value) })}
                  size="small"
                  inputProps={{ step: 0.1 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Pricing & Notifications */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card, mb: 3 }}>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
              Pricing Configuration
            </Typography>

            <TextField
              fullWidth
              label="Gas Price (₱/m³)"
              type="number"
              value={pricing.gas_price_per_m3}
              onChange={(e) => setPricing({ ...pricing, gas_price_per_m3: parseFloat(e.target.value) })}
              size="small"
              sx={{ mb: 2 }}
              inputProps={{ step: 0.01 }}
            />

            <TextField
              fullWidth
              label="Electricity Rate (₱/kWh)"
              type="number"
              value={pricing.electricity_rate_per_kwh}
              onChange={(e) => setPricing({ ...pricing, electricity_rate_per_kwh: parseFloat(e.target.value) })}
              size="small"
              sx={{ mb: 2 }}
              inputProps={{ step: 0.01 }}
            />

            <TextField
              fullWidth
              label="Feedstock Cost (₱/kg)"
              type="number"
              value={pricing.feedstock_cost_per_kg}
              onChange={(e) => setPricing({ ...pricing, feedstock_cost_per_kg: parseFloat(e.target.value) })}
              size="small"
              inputProps={{ step: 0.01 }}
            />

            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.neutral[700], mt: 2, mb: 1.5 }}>
              Billing Rates (charged to Dole Philippines)
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: colors.neutral[500], mb: 2 }}>
              Rates are date-effective. Past reports use the rate that was active during that period.
            </Typography>

            {/* Add new rate form */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Type</InputLabel>
                <Select value={newRate.rateType} label="Type"
                  onChange={(e) => setNewRate({ ...newRate, rateType: e.target.value })}>
                  <MenuItem value="power">Power (₱/kWh)</MenuItem>
                  <MenuItem value="gas">Gas (₱/Nm³)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Rate"
                type="number"
                size="small"
                value={newRate.rateValue}
                onChange={(e) => setNewRate({ ...newRate, rateValue: e.target.value })}
                sx={{ width: 120 }}
                inputProps={{ step: 0.01 }}
              />
              <TextField
                label="Effective Date"
                type="date"
                size="small"
                value={newRate.effectiveDate}
                onChange={(e) => setNewRate({ ...newRate, effectiveDate: e.target.value })}
                sx={{ width: 170 }}
              />
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                disabled={savingRate || !newRate.rateValue || !newRate.effectiveDate}
                onClick={handleSaveRate}
                sx={{ bgcolor: colors.primary[600], height: 40 }}>
                Add
              </Button>
            </Box>

            {/* Rate history table */}
            {billingRates.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Rate</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Effective Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }} align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billingRates.map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <Chip size="small"
                            label={r.rateType === 'power' ? 'Power (₱/kWh)' : 'Gas (₱/Nm³)'}
                            color={r.rateType === 'power' ? 'primary' : 'success'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {Number(r.rateValue).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {new Date(r.effectiveDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="error" onClick={() => handleDeleteRate(r.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
              Notifications & Updates
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={notifications.enable_alerts}
                  onChange={(e) => setNotifications({ ...notifications, enable_alerts: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: colors.primary[600],
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: colors.primary[600],
                    },
                  }}
                />
              }
              label="Enable Alert Notifications"
              sx={{ mb: 2, display: 'block' }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={notifications.enable_email_reports}
                  onChange={(e) => setNotifications({ ...notifications, enable_email_reports: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: colors.primary[600],
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: colors.primary[600],
                    },
                  }}
                />
              }
              label="Enable Daily Email Reports"
              sx={{ mb: 3, display: 'block' }}
            />

            <TextField
              fullWidth
              label="Dashboard Refresh Interval (minutes)"
              type="number"
              value={notifications.refresh_interval}
              onChange={(e) => setNotifications({ ...notifications, refresh_interval: parseInt(e.target.value) })}
              size="small"
              inputProps={{ min: 1, max: 60 }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons for System Configuration */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={handleReset}
              sx={{
                borderColor: colors.neutral[300],
                color: colors.neutral[700],
                '&:hover': {
                  borderColor: colors.neutral[400],
                  backgroundColor: colors.neutral[50],
                },
              }}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{
                background: gradients.primary,
                color: '#ffffff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                },
              }}
            >
              Save Settings
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
    </>
      )}

      {/* Data Connections Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
                MQTT Broker Configuration
              </Typography>

              <TextField
                fullWidth
                label="MQTT Broker URL"
                value={mqttConfig.broker_url}
                onChange={(e) => setMqttConfig({ ...mqttConfig, broker_url: e.target.value })}
                size="small"
                sx={{ mb: 2 }}
                helperText="Format: mqtt://host:port or mqtts://host:port"
              />

              <TextField
                fullWidth
                label="Client ID Prefix"
                value={mqttConfig.client_id_prefix}
                onChange={(e) => setMqttConfig({ ...mqttConfig, client_id_prefix: e.target.value })}
                size="small"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Username (optional)"
                value={mqttConfig.username || ''}
                onChange={(e) => setMqttConfig({ ...mqttConfig, username: e.target.value })}
                size="small"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Password (optional)"
                type="password"
                value={mqttConfig.password || ''}
                onChange={(e) => setMqttConfig({ ...mqttConfig, password: e.target.value })}
                size="small"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={mqttConfig.enabled}
                    onChange={(e) => setMqttConfig({ ...mqttConfig, enabled: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: colors.primary[600],
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: colors.primary[600],
                      },
                    }}
                  />
                }
                label="Enable MQTT Connection"
                sx={{ mb: 2 }}
              />

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Subscribed Topics:
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                <Typography variant="caption" display="block">• biogas/feedstock/+</Typography>
                <Typography variant="caption" display="block">• biogas/digester/+/+</Typography>
                <Typography variant="caption" display="block">• biogas/power/+</Typography>
                <Typography variant="caption" display="block">• biogas/gas/+</Typography>
              </Box>

              <Button
                fullWidth
                variant="outlined"
                startIcon={testingMqtt ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
                onClick={handleTestMqtt}
                disabled={testingMqtt}
                sx={{
                  borderColor: colors.primary[600],
                  color: colors.primary[600],
                  '&:hover': {
                    borderColor: colors.primary[700],
                    backgroundColor: colors.primary[50],
                  },
                }}
              >
                {testingMqtt ? 'Testing Connection...' : 'Test MQTT Connection'}
              </Button>

              {mqttTestResult && (
                <Alert 
                  severity={mqttTestResult.success ? 'success' : 'error'} 
                  sx={{ mt: 2 }}
                >
                  {mqttTestResult.message}
                </Alert>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card, mb: 3 }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
                Database Connection
              </Typography>

              <TextField
                fullWidth
                label="Database Host"
                value={databaseConfig.host}
                onChange={(e) => setDatabaseConfig({ ...databaseConfig, host: e.target.value })}
                size="small"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Database Port"
                value={databaseConfig.port}
                onChange={(e) => setDatabaseConfig({ ...databaseConfig, port: parseInt(e.target.value) })}
                type="number"
                size="small"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Database Name"
                value={databaseConfig.database}
                onChange={(e) => setDatabaseConfig({ ...databaseConfig, database: e.target.value })}
                size="small"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Database User"
                value={databaseConfig.user}
                onChange={(e) => setDatabaseConfig({ ...databaseConfig, user: e.target.value })}
                size="small"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Database Password"
                type="password"
                value={databaseConfig.password || ''}
                onChange={(e) => setDatabaseConfig({ ...databaseConfig, password: e.target.value })}
                size="small"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                variant="outlined"
                startIcon={testingDatabase ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
                onClick={handleTestDatabase}
                disabled={testingDatabase}
                sx={{
                  borderColor: colors.primary[600],
                  color: colors.primary[600],
                  '&:hover': {
                    borderColor: colors.primary[700],
                    backgroundColor: colors.primary[50],
                  },
                  mb: 2,
                }}
              >
                {testingDatabase ? 'Testing Connection...' : 'Test Database Connection'}
              </Button>

              {databaseTestResult && (
                <Alert 
                  severity={databaseTestResult.success ? 'success' : 'error'}
                >
                  {databaseTestResult.message}
                </Alert>
              )}

              {databaseTestResult?.success && (
                <Chip
                  label="Connected"
                  color="success"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Paper>

            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
                WebSocket Configuration
              </Typography>

              <TextField
                fullWidth
                label="WebSocket Port"
                value={websocketConfig.port}
                onChange={(e) => setWebsocketConfig({ ...websocketConfig, port: parseInt(e.target.value) })}
                type="number"
                size="small"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={websocketConfig.enabled}
                    onChange={(e) => setWebsocketConfig({ ...websocketConfig, enabled: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: colors.primary[600],
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: colors.primary[600],
                      },
                    }}
                  />
                }
                label="Enable Real-time Updates"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                variant="outlined"
                startIcon={testingWebSocket ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
                onClick={handleTestWebSocket}
                disabled={testingWebSocket}
                sx={{
                  borderColor: colors.primary[600],
                  color: colors.primary[600],
                  '&:hover': {
                    borderColor: colors.primary[700],
                    backgroundColor: colors.primary[50],
                  },
                  mb: 2,
                }}
              >
                {testingWebSocket ? 'Testing Configuration...' : 'Test WebSocket Port'}
              </Button>

              {websocketTestResult && (
                <Alert 
                  severity={websocketTestResult.success ? 'success' : 'error'}
                >
                  {websocketTestResult.message}
                </Alert>
              )}

              {websocketTestResult?.success && (
                <Chip
                  label="Active Connections: 1"
                  color="success"
                  size="small"
                />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<RestoreIcon />}
                  onClick={handleReset}
                  sx={{
                    borderColor: colors.neutral[300],
                    color: colors.neutral[700],
                    '&:hover': {
                      borderColor: colors.neutral[400],
                      backgroundColor: colors.neutral[50],
                    },
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveConnections}
                  sx={{
                    background: gradients.primary,
                    color: '#ffffff',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    },
                  }}
                >
                  Save Connection Settings
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Database Mapping Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900] }}>
                  Database Table Mappings
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={discoveringSchema ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDiscoverSchema();
                    }}
                    disabled={discoveringSchema}
                    sx={{
                      borderColor: colors.primary[600],
                      color: colors.primary[600],
                    }}
                  >
                    {discoveringSchema ? 'Discovering...' : 'Discover Schema'}
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddMapping();
                    }}
                    disabled={!databaseSchema}
                    sx={{
                      background: gradients.primary,
                      color: '#ffffff',
                    }}
                  >
                    Add Mapping
                  </Button>
                </Box>
              </Box>

              {!databaseSchema && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Click "Discover Schema" to scan your connected database and see available tables and columns.
                </Alert>
              )}

              {databaseSchema && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Found {Object.keys(databaseSchema).length} tables in database. You can now create mappings.
                </Alert>
              )}

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Source Table</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Target Entity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Columns Mapped</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Filter</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableMappings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: colors.neutral[500] }}>
                          No table mappings configured. Click "Add Mapping" to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableMappings.map((mapping, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip label={mapping.source_table} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={mapping.target_entity} 
                              size="small"
                              sx={{ 
                                backgroundColor: colors.primary[100],
                                color: colors.primary[700],
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {mapping.columns.length} columns
                          </TableCell>
                          <TableCell>
                            {mapping.filter ? (
                              <Chip label={mapping.filter} size="small" variant="outlined" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">None</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleEditMapping(mapping)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteMapping(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {databaseSchema && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
                <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
                  Available Database Tables
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(databaseSchema).map(([tableName, columns]) => (
                    <Grid item xs={12} md={6} lg={4} key={tableName}>
                      <Paper 
                        sx={{ 
                          p: 2, 
                          border: `1px solid ${colors.neutral[200]}`,
                          borderRadius: borderRadius.card,
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                          {tableName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {columns.length} columns
                        </Typography>
                        <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                          {columns.map((col, idx) => (
                            <Typography 
                              key={idx} 
                              variant="caption" 
                              display="block"
                              sx={{ 
                                py: 0.5,
                                color: colors.neutral[600],
                              }}
                            >
                              • {col.name} ({col.type})
                            </Typography>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSaveMappings();
                  }}
                  disabled={tableMappings.length === 0}
                  sx={{
                    background: gradients.primary,
                    color: '#ffffff',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    },
                  }}
                >
                  Save Mappings
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tag Import & Sync Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          {loading ? (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            </Grid>
          ) : (
            <>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
                Tag Discovery Configuration
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Tag Table Name"
                    value={tagTableName}
                    onChange={(e) => setTagTableName(e.target.value)}
                    helperText="Name of the table containing tags"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Tag Name Column"
                    value={tagColumnName}
                    onChange={(e) => setTagColumnName(e.target.value)}
                    helperText="Column containing tag names"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Unit Column (Optional)"
                    value={tagUnitColumn}
                    onChange={(e) => setTagUnitColumn(e.target.value)}
                    helperText="Column containing measurement units"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleDiscoverTags}
                  disabled={discoveringTags || !tagTableName || !tagColumnName}
                  sx={{ background: gradients.primary, color: '#ffffff' }}
                >
                  {discoveringTags ? <CircularProgress size={24} /> : 'Discover Tags'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAutoMapTags}
                  disabled={autoMappingTags || discoveredTags.length === 0}
                  sx={{ background: gradients.production, color: '#ffffff' }}
                >
                  {autoMappingTags ? <CircularProgress size={24} /> : 'Auto-Map Tags'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleManualSync}
                  disabled={syncingData}
                  startIcon={syncingData ? <CircularProgress size={20} /> : null}
                >
                  Manual Data Sync
                </Button>
              </Box>

              {discoveredTags.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Discovered {discoveredTags.length} tags from {tagTableName}
                </Alert>
              )}
            </Paper>
          </Grid>

          {tagMappingSuggestions.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900] }}>
                    Tag Mapping Suggestions ({selectedMappings.length} selected)
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleSaveTagMappings}
                    disabled={selectedMappings.length === 0}
                    sx={{ background: gradients.primary, color: '#ffffff' }}
                  >
                    Save Selected Mappings
                  </Button>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedMappings.length === tagMappingSuggestions.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMappings(tagMappingSuggestions);
                              } else {
                                setSelectedMappings([]);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Tag Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Target Entity</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Target Field</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Metadata</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tagMappingSuggestions.map((suggestion: any) => (
                        <TableRow key={suggestion.tag_name}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedMappings.some(m => m.tag_name === suggestion.tag_name)}
                              onChange={() => toggleMappingSelection(suggestion)}
                            />
                          </TableCell>
                          <TableCell>{suggestion.tag_name}</TableCell>
                          <TableCell>{suggestion.unit || '-'}</TableCell>
                          <TableCell>{suggestion.target_entity}</TableCell>
                          <TableCell>{suggestion.target_field}</TableCell>
                          <TableCell>
                            <Chip
                              label={`${(suggestion.confidence * 100).toFixed(0)}%`}
                              size="small"
                              color={suggestion.confidence > 0.8 ? 'success' : suggestion.confidence > 0.6 ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {suggestion.metadata && Object.keys(suggestion.metadata).length > 0 && (
                              <Typography variant="caption">
                                {Object.entries(suggestion.metadata).map(([k, v]) => `${k}: ${v}`).join(', ')}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
            </>
          )}
        </Grid>
      )}

      {/* User Management (RBAC) Tab */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900] }}>
                  User Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenUserDialog(true)}
                  sx={{
                    background: gradients.primary,
                    color: '#ffffff',
                  }}
                >
                  Add New User
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 20, color: colors.neutral[500] }} />
                            {user.username}
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {user.roles?.map((role: string) => (
                              <Chip
                                key={role}
                                label={role}
                                size="small"
                                sx={{
                                  backgroundColor: role === 'admin' ? colors.primary[100] : colors.neutral[100],
                                  color: role === 'admin' ? colors.primary[700] : colors.neutral[700],
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={user.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => {
                            setEditingUser(user);
                            setOpenUserDialog(true);
                          }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={user.username === 'admin'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
                Roles & Permissions
              </Typography>

              {roles.map((role) => (
                <Box key={role.id} sx={{ mb: 3, pb: 2, borderBottom: `1px solid ${colors.neutral[200]}` }}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {role.role_name}
                    <Chip label={role._count?.users || 0} size="small" sx={{ ml: 1 }} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {role.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {role.permissions?.slice(0, 6).map((perm: any) => (
                      <Chip
                        key={perm.id}
                        label={perm.permission_name}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                    {role.permissions?.length > 6 && (
                      <Chip
                        label={`+${role.permissions.length - 6} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900], mb: 3 }}>
                Permission Matrix
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Available Permissions:
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['view:dashboard', 'view:production', 'view:digester', 'view:analytics', 'view:reports', 
                  'edit:settings', 'edit:config', 'manage:users', 'manage:alerts', 'control:digester', 'export:data'].map((perm) => (
                  <Box
                    key={perm}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: colors.neutral[50],
                      border: `1px solid ${colors.neutral[200]}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {perm}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Database Mapping Dialog */}
      <Dialog 
        open={openMappingDialog} 
        onClose={() => {
          setOpenMappingDialog(false);
          setEditingMapping(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingMapping?.source_table ? 'Edit Table Mapping' : 'Create New Table Mapping'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Source Table</InputLabel>
            <Select
              value={editingMapping?.source_table || ''}
              onChange={(e) => setEditingMapping(editingMapping ? { ...editingMapping, source_table: e.target.value } : null)}
              label="Source Table"
            >
              {databaseSchema && Object.keys(databaseSchema).map((tableName) => (
                <MenuItem key={tableName} value={tableName}>
                  {tableName} ({databaseSchema[tableName].length} columns)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Target Entity</InputLabel>
            <Select
              value={editingMapping?.target_entity || ''}
              onChange={async (e) => {
                const entity = e.target.value;
                setEditingMapping(editingMapping ? { ...editingMapping, target_entity: entity, columns: [] } : null);
                if (entity) {
                  await loadTargetFieldsForEntity(entity);
                }
              }}
              label="Target Entity"
            >
              {targetEntities.map((entity) => (
                <MenuItem key={entity} value={entity}>
                  {entity}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Filter Condition (optional)"
            value={editingMapping?.filter || ''}
            onChange={(e) => setEditingMapping(editingMapping ? { ...editingMapping, filter: e.target.value } : null)}
            placeholder="e.g., sensor_type = 'temperature'"
            sx={{ mb: 2 }}
            helperText="SQL WHERE clause condition"
          />

          <TextField
            fullWidth
            label="Order By (optional)"
            value={editingMapping?.order_by || ''}
            onChange={(e) => setEditingMapping(editingMapping ? { ...editingMapping, order_by: e.target.value } : null)}
            placeholder="e.g., timestamp DESC"
            sx={{ mb: 2 }}
            helperText="SQL ORDER BY clause"
          />

          <Typography sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
            Column Mappings
          </Typography>

          {editingMapping?.target_entity && targetFields.length > 0 && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: colors.neutral[50], borderRadius: 1, border: `1px solid ${colors.neutral[200]}` }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Target Fields Summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {targetFields.map((field) => {
                  const isMapped = editingMapping.columns.some(c => c.target_field === field.name);
                  return (
                    <Chip
                      key={field.name}
                      label={field.name}
                      size="small"
                      color={field.required && !isMapped ? 'error' : isMapped ? 'success' : 'default'}
                      variant={isMapped ? 'filled' : 'outlined'}
                      icon={isMapped ? <CheckCircleOutlineIcon /> : undefined}
                    />
                  );
                })}
              </Box>
              {targetFields.filter(f => f.required && !editingMapping.columns.some(c => c.target_field === f.name)).length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {targetFields.filter(f => f.required && !editingMapping.columns.some(c => c.target_field === f.name)).length} required field(s) not mapped yet
                </Alert>
              )}
            </Box>
          )}

          {editingMapping?.source_table && databaseSchema?.[editingMapping.source_table] && (
            <Box sx={{ maxHeight: 400, overflowY: 'auto', border: `1px solid ${colors.neutral[200]}`, borderRadius: 1, p: 2 }}>
              {databaseSchema[editingMapping.source_table].map((column, idx) => {
                const currentMapping = editingMapping.columns.find(c => c.source_column === column.name);
                const mappedField = targetFields.find(f => f.name === currentMapping?.target_field);
                
                return (
                  <Box key={idx} sx={{ mb: 2, pb: 2, borderBottom: idx < databaseSchema[editingMapping.source_table].length - 1 ? `1px solid ${colors.neutral[100]}` : 'none' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {column.name} <Chip label={column.type} size="small" sx={{ ml: 1 }} />
                      </Typography>
                      {mappedField && (
                        <Chip 
                          label={mappedField.required ? 'Required' : 'Optional'} 
                          size="small" 
                          color={mappedField.required ? 'error' : 'default'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {editingMapping.target_entity && targetFields.length > 0 ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>Map to field</InputLabel>
                        <Select
                          value={currentMapping?.target_field || ''}
                          onChange={(e) => {
                            if (!editingMapping) return;
                            const existingIdx = editingMapping.columns.findIndex(c => c.source_column === column.name);
                            const newColumns = [...editingMapping.columns];
                            
                            if (e.target.value) {
                              if (existingIdx >= 0) {
                                newColumns[existingIdx] = { ...newColumns[existingIdx], target_field: e.target.value };
                              } else {
                                newColumns.push({ 
                                  source_column: column.name, 
                                  target_field: e.target.value,
                                  data_type: column.type 
                                });
                              }
                            } else if (existingIdx >= 0) {
                              newColumns.splice(existingIdx, 1);
                            }
                            
                            setEditingMapping({ ...editingMapping, columns: newColumns });
                          }}
                          label="Map to field"
                        >
                          <MenuItem value="">
                            <em>None (skip this column)</em>
                          </MenuItem>
                          {targetFields.map((field) => (
                            <MenuItem key={field.name} value={field.name}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {field.name}
                                  </Typography>
                                  <Chip label={field.type} size="small" />
                                  {field.required && (
                                    <Chip label="Required" size="small" color="error" variant="outlined" />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {field.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        label="Map to field"
                        placeholder="Enter target field name or select target entity first"
                        value={currentMapping?.target_field || ''}
                        onChange={(e) => {
                          if (!editingMapping) return;
                          const existingIdx = editingMapping.columns.findIndex(c => c.source_column === column.name);
                          const newColumns = [...editingMapping.columns];
                          
                          if (e.target.value) {
                            if (existingIdx >= 0) {
                              newColumns[existingIdx] = { ...newColumns[existingIdx], target_field: e.target.value };
                            } else {
                              newColumns.push({ 
                                source_column: column.name, 
                                target_field: e.target.value,
                                data_type: column.type 
                              });
                            }
                          } else if (existingIdx >= 0) {
                            newColumns.splice(existingIdx, 1);
                          }
                          
                          setEditingMapping({ ...editingMapping, columns: newColumns });
                        }}
                        disabled={!editingMapping.target_entity}
                        helperText={!editingMapping.target_entity ? "Select a target entity to enable mapping" : ""}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {!editingMapping?.source_table && (
            <Alert severity="info">
              Select a source table to view and map its columns
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenMappingDialog(false);
            setEditingMapping(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveMapping}
            disabled={!editingMapping?.source_table || !editingMapping?.target_entity || editingMapping.columns.length === 0}
            sx={{ background: gradients.primary }}
          >
            Save Mapping
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Create/Edit Dialog */}
      <Dialog open={openUserDialog} onClose={() => {
        setOpenUserDialog(false);
        setEditingUser(null);
        setNewUser({ username: '', email: '', password: '', roles: [] });
      }}>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <TextField
            fullWidth
            label="Username"
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          {!editingUser && (
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              sx={{ mb: 2 }}
            />
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Roles</InputLabel>
            <Select
              multiple
              value={newUser.roles}
              onChange={(e) => setNewUser({ ...newUser, roles: e.target.value as string[] })}
              input={<OutlinedInput label="Roles" />}
              renderValue={(selected) => selected.join(', ')}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.role_name}>
                  <Checkbox checked={newUser.roles.indexOf(role.role_name) > -1} />
                  <ListItemText primary={role.role_name} secondary={role.description} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenUserDialog(false);
            setEditingUser(null);
            setNewUser({ username: '', email: '', password: '', roles: [] });
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            sx={{ background: gradients.primary }}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{
            backgroundColor: colors.status.success,
            color: '#ffffff',
          }}
        >
          Settings saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{
            backgroundColor: colors.status.error,
            color: '#ffffff',
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
